<template>
  <div class="_content md-elevation-1">
    <div class="md-autocomplete-loading" v-if="isPromisePending">
      <md-progress-spinner :md-diameter="40" :md-stroke="4" md-mode="indeterminate" />
    </div>
    <collapse :collapse="['FILTERS']" icon="keyboard_arrow_down" :defaultActive="defaultActiveCollapseIndex">
      <template slot="md-collapse-pane-1">
        <div class="md-layout">
          <div class="md-layout-item md-small-size-100">
            <md-field class="_uiKitInput">
              <label>Date Field To Filter</label>
              <md-select v-model="form.dateField">
                <md-option
                  v-for="(_option, _index) in formConfig.dateField.options"
                  :key="_index"
                  :value="_option.key"
                  >{{ _option.label }}</md-option
                >
              </md-select>
            </md-field>
          </div>

          <div class="md-layout-item md-small-size-100">
            <md-datepicker md-immediately v-model="form.dateFrom" class="_uiKitInput">
              <label>From</label>
            </md-datepicker>
          </div>

          <div class="md-layout-item md-small-size-100">
            <md-datepicker md-immediately v-model="form.dateTo" class="_uiKitInput">
              <label>To</label>
            </md-datepicker>
          </div>

          <div class="md-layout-item md-size-100">
            <ReportSearchModal v-model="form.portfolio" :config="formConfig.portfolio.config"></ReportSearchModal>
          </div>

          <div class="md-layout-item md-size-50 md-small-size-100">
            <md-button class="md-primary space" @click="search"><md-icon>search</md-icon>Search </md-button>
            <md-button class="space" @click="resetForm"><md-icon>search</md-icon>Clear Filters </md-button>
            <md-button :disabled="disableDownload" @click="downloadCSV"><md-icon>assignment_returned</md-icon>Download </md-button>
          </div>
        </div>
      </template>
    </collapse>
    
    <md-snackbar md-position="center" :md-active.sync="isSnackbarOpen" md-persistent>
      <span>{{ snackbarMsg }}</span>
    </md-snackbar>
  </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import Amplify, { Auth, API, graphqlOperation, JS } from "aws-amplify";
import * as queries from "@/graphql/queries";
import * as queries_custom from "@/graphql/queries_custom";

import { Collapse } from "@/components";

import ReportSearchModal from "./ReportSearchModal";
import ReportConfigDefaults from "./configs/ReportConfigDefaults";
import DownloadCSV from "./DownloadCSV";

export default {
  components: {
    Collapse,
    ReportSearchModal,
  },
  props: {
    config: {
      type: Object,
    },
    filterHook: {
      required: true,
    },
  },
  data() {
    const defaults = _.cloneDeep(ReportConfigDefaults);
    return {
      disableDownload: true,
      defaultActiveCollapseIndex: 1,
      form: defaults.form,
      formConfig: defaults.formConfig,
      queryParams: defaults.queryParams,
      esQuery: null,
      allPortfolios: [],
      allServices: [],
      isPromisePending: false,
      queryParams: null,
      isSnackbarOpen: false,
      snackbarMsg: '',
    };
  },
  /**
   * Component mounted - reset form
   */
  beforeMount() {
    this.resetForm();
  },
  methods: {
    /**
     * Get all portfolio items - will be used if no portfolio items selected
     * @returns {void}
     */
    getAllPortfolios() {
      // mark pending promise
      this.isPromisePending = true;
      
      // get query name and response field name
      let listQueryId = ReportConfigDefaults.listQueryId;
      let responseFieldName = ReportConfigDefaults.responseFieldName;
      if (this.config && this.config.listQueryId) {
        listQueryId = this.config.listQueryId;
        responseFieldName = listQueryId;
        if (this.config.responseFieldName) {
          responseFieldName = this.config.responseFieldName;
        }
      }
      
      // get query 
      let query = queries[listQueryId];
      if (!query) {
        query = queries_custom[listQueryId];
      }
      
      // query not found - bail
      if (!query) {
        this.isPromisePending = false;
        return;
      }
      
      // call graphql and fetch portfolios
      let operation = graphqlOperation(query, { limit: 999 });
      API.graphql(operation)
        .then((response) => {
          const items = this.$reduce(response, `data.${responseFieldName}.items`);
          if (items && items.length) {
            this.allPortfolios = items;
          }
          this.isPromisePending = false;
        })
        .catch(err => {
          console.log('Error fetching all portfolios:', err);
          this.isPromisePending = false;
        });
    },
    /**
     * Reset form fields and form config using defaults and provided config
     * @returns {void}
     */
    resetForm() {
      const config = this.config;
      const defaults = _.cloneDeep(ReportConfigDefaults);

      // dateField
      let dateField = this.$reduce(config, "form.dateField");
      if (undefined === dateField) {
        dateField = defaults.form.dateField;
      }

      // dateFrom
      let dateFrom = this.$reduce(config, "form.dateFrom");
      if (undefined === dateFrom) {
        dateFrom = defaults.form.dateFrom;
      }

      // dateTo
      let dateTo = this.$reduce(config, "form.dateTo");
      if (undefined === dateTo) {
        dateTo = defaults.form.dateTo;
      }

      // portfolio
      let portfolio = this.$reduce(config, "form.portfolio");
      if (undefined === portfolio) {
        portfolio = defaults.form.portfolio;
      }

      // dateFieldOptions
      let dateFieldOptions = this.$reduce(config, "formConfig.dateField.options");
      if (undefined === dateFieldOptions) {
        dateFieldOptions = defaults.formConfig.dateField.options;
      }

      // portfolioConfig
      let portfolioConfig = this.$reduce(this, "config.formConfig.portfolio.config");
      if (undefined === portfolioConfig) {
        portfolioConfig = defaults.formConfig.portfolio.config;
      }

      // set form data
      this.$set(this.form, "dateField", dateField);
      this.$set(this.form, "dateFrom", dateFrom);
      this.$set(this.form, "dateTo", dateTo);
      this.$set(this.form, "portfolio", portfolio);

      // set form config
      this.$set(this.formConfig.dateField, "options", dateFieldOptions);
      this.$set(this.formConfig.portfolio, "config", portfolioConfig);
    },
    /**
     * Prepare search filter and emit new value
     * We need to reset query params to make sure we don't reuse old values
     * @returns {void}
     */
    async search() {
      if (!this.isPortfolioSelected()) {
        return;
      }
      this.isPromisePending = true;
      this.$emit("input", { isReady: false });
      this.queryParams = _.cloneDeep(ReportConfigDefaults.queryParams);
      await this.filterServices();
      this.buildEsQuery();
      this.$emit("input", { isReady: true, filter: this.esQuery });
      this.isPromisePending = false;
      this.disableDownload = false;
    },
    /**
     * Add filters to query
     * @returns {Mixed} Promise or Boolean
     */
    async filterServices() {
      return new Promise(async (resolve, reject) => {
        const ids = [];
        const form = this.form;
        const dateField = form.dateField;
        const dateFrom = form.dateFrom;
        const dateTo = form.dateTo;
        this.allServices = [];
        
        let portfolioItems = this.allPortfolios;
        if (form.portfolio && form.portfolio.length) {
          portfolioItems = form.portfolio;
        }
        
        for (let i = 0; i < portfolioItems.length; i++) {
          const _portfolio = portfolioItems[i];
          let serviceItems = this.$reduce(_portfolio, 'services.items');
          let services = [];
          
          // get services from nested object
          serviceItems.forEach(_item => {
            if (_item.service && _item.service.id) {
              services.push(_item.service);
            }
          });
          
          // no services found - bail
          if (!services || !services.length) {
            continue;
          }
          
          // if dateField is set to Application Received then add 
          if ('applicationReceived' == dateField) {
            await this.addReceivedDate(services);
          }
          
          // filter all services where date field is not set
          if (dateFrom || dateTo) {
            services = services.filter(_service => {
              return _service[dateField] && moment(_service[dateField]).isValid();
            });
          }
          
          // filter from date
          if (dateFrom) {
            services = services.filter(_service => {
              const sourceDate = moment(_service[dateField]).startOf('day');
              const targetDate = moment(dateFrom).startOf('day');
              return sourceDate.isSameOrAfter(targetDate);
            });
          }
          
          // filter to date
          if (dateTo) {
            services = services.filter(_service => {
              const sourceDate = moment(_service[dateField]).startOf('day');
              const targetDate = moment(dateTo).startOf('day');
              return sourceDate.isSameOrBefore(targetDate);
            });
          }
          
          services.forEach(_service => {
            // add portfolio for reference if not alredy present
            if (!_service.portfolio || !_service.portfolio.id) {
              _service.portfolio = _.cloneDeep(_portfolio);
            }
            this.allServices.push(_service)
          });
        }
        
        await this.filterHook(this.allServices, this.queryParams, form);
        resolve();
      });
    },
    /**
     * Get change logs and if status change to “App Received” then add date
     * @param {Array} services list of services
     * @returns Promise
     */
    addReceivedDate(services) {
      return new Promise((resolve, reject) => {
        this.getChangeLogs(services)
          .then(response => {
            let logs = this.$reduce(response, 'data.searchChanges.items');
            this.searchLogsAndAddReceivedDate(services, logs);
            resolve();
          })
          .catch(err => {
            console.log('Error fetching change logs for services', err);
            resolve();
          });
      });
    },
    /**
     * For the provided services - fetch change logs
     * @param {Array} services list of services
     * @returns {Promise}
     */
    getChangeLogs(services) {
      const ids = [];
      services.forEach(_service => {
        ids.push({ match: { modelId: _service.id } });
      });
      const params = {
        limit: 999,
        filter: {
          esQuery: JSON.stringify({ 
            bool: { 
              must: [
                { match: { modelName: 'service' } },
                { bool: { should: ids } }
              ]
            } 
          })
        }
      };
      return API.graphql(graphqlOperation(queries.searchChanges, params));
    },
    /**
     * For the provided services - scan change logs and if status was changed
     * to app_received then add the change date as applicationReceived date
     * @param {Array} services list of services
     * @returns {void}
     */
    searchLogsAndAddReceivedDate(services, logs) {
      if (!logs || !logs.length) {
        return;
      }
      
      // filter logs where status was changed to app_received
      logs = logs.filter(_log => {
        try {
          _log.change = JSON.parse(_log.change);
        } catch (e) {
          _log.change = {};
        }
        return _log.change.status && 'app_received' == _log.change.status.toLowerCase();
      });
      
      // sort logs by latest first
      const sortedLogs = _.sortBy(logs, 'createdAt').reverse();
      
      // group logs by service id
      const groupedLogs = _.groupBy(logs, 'modelId');
      
      // loop through services and add applicationReceived date if found
      services.forEach(_service => {
        _service.applicationReceived = this.$reduce(groupedLogs, `${_service.id}.0.createdAt`);
      });
    },
    /**
     * Build esQuery for graphql call
     * @returns {void}
     */
    async buildEsQuery(download) {
      let params = this.queryParams;
      Object.keys(params).map((_key) => {
        if (!params[_key] || !params[_key].length) {
          delete params[_key];
        }
      });
      if (Object.keys(params).length) {
        this.esQuery = { filter: { esQuery: JSON.stringify({ bool: params }) } };
        if(download)
          this.esQuery = { limit: 9999, filter: { esQuery: JSON.stringify({ bool: params }) } };
      } else {
        params = null;
        this.esQuery = null;
      }
    },
    async downloadCSV() {
      this.isPromisePending = true;
      this.queryParams = _.cloneDeep(ReportConfigDefaults.queryParams);
      await this.filterServices();
      this.buildEsQuery(true);
      
      // get query name and response field name
      let listQueryId = ReportConfigDefaults.listQueryId;
      let responseFieldName = ReportConfigDefaults.responseFieldName;
      if (this.config.csv && this.config.csv.listQueryId) {
        listQueryId = this.config.csv.listQueryId;
        responseFieldName = listQueryId;
        if (this.config.csv.responseFieldName) {
          responseFieldName = this.config.csv.responseFieldName;
        }
      }
      
      // get query 
      let query = queries[listQueryId];
      if (!query) {
        query = queries_custom[listQueryId];
      }
      
      // query not found - bail
      if (!query) {
        this.isPromisePending = false;
        this.isPromisePending = false;
        return;
      }
      
      this.queryParams['limit'] = 999;

      let operation = graphqlOperation(query, this.esQuery);
      API.graphql(operation)
      .then((response) => {
        this.isPromisePending = false;
        let formattedResponse = this.config.formatResponse(response.data);
        DownloadCSV.download(this.config.csv.reportName, formattedResponse, this.config.columns)
      })
      .catch(err => {
        this.isPromisePending = false;
        console.log('Error fetching data:', err);
      });
    },
    isPortfolioSelected() {
      if (!this.form.portfolio || !this.form.portfolio.length) {
        this.snackbarMsg = 'Please select at least one portfolio.';
        this.isSnackbarOpen = true;
        return false;
      }
      return true;
    },
  },
  watch: {
    config: {
      deep: true,
      handler: function() {
        this.resetForm();
      },
    },
  },
};
</script>

<style lang="scss">
._report .list-header-hidden {
  display: none;
}
._content {
  .md-collapse-label {
    padding: 20px;
    &::after {
      bottom: 0;
    }
  }
  .md-collapse-content {
    padding: 15px 10px 20px 10px;
  }
}
.space {
  margin-right: 5px;
}
</style>
